import React, {useState} from "react";
import {IProtocolStep, isProtocolStep as isAProtocolStep} from "@/protocols/types/protocol.ts";
import {IProtocolStepDefinition} from "@/protocols/types/protocolDefinitions.ts";
import {TSubjectId} from "@/common/types";
import classes from "./FileBadge.module.css";
import {
    ActionIcon,
    Box,
    Popover,
    Text,
    Tooltip,
} from "@mantine/core";
import {StepFileManager} from "@/protocols/studies/steps/StepFileManager.tsx";
import {IconFile, IconPin, IconPinnedOff} from "@tabler/icons-react";
import cx from "clsx";


/**
 * Renders a file count badge with hover popover for file manager.
 * Opens automatically when files are dragged over it.
 *
 * This is the primary file badge component used in both:
 * - Protocol execution/recorder mode (IProtocolStep with timezone)
 * - Protocol planning/definition mode (IProtocolStepDefinition without timezone)
 */
export const FileBadge: React.FC<{
    step: IProtocolStep | IProtocolStepDefinition;
    subjectId?: TSubjectId;
    disabled?: boolean;
    processing?: boolean,
    waiting?: boolean,
}> = ({step, subjectId, processing=false, waiting=false, disabled = false}) => {
    // Check if it's IProtocolStep
    const isProtocolStep = isAProtocolStep(step);
    const fileCount = isProtocolStep ? ((step as IProtocolStep).transient?.fileData?.files?.length || 0) : 0;
    const uploadProgress = isProtocolStep ? (step as IProtocolStep).transient?.fileData?.uploadProgress : undefined;
    const uploadsInProgress = Object.keys(uploadProgress || {}).length;
    const [opened, setOpened] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isPinned, setIsPinned] = useState(false);

    const handleClose = () => {
        if (!isPinned) {
            setOpened(false);
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Check if dragging files and not disabled
        if (!disabled && e.dataTransfer.types.includes('Files')) {
            setIsDragOver(true);
            setOpened(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only close if leaving the badge itself (not entering the dropdown)
        const target = e.currentTarget as HTMLElement;
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!target.contains(relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    return (
        <Popover
            position="bottom-end"
            withArrow
            shadow="md"
            opened={opened}
            onChange={setOpened}
            withinPortal={true}
            returnFocus={false}
            offset={0}
            zIndex={1000}
        >
            <Popover.Target>
                <Box
                    className={cx(classes.fileBadge, processing && classes.p, waiting && classes.waiting)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setOpened(true)}
                    onMouseLeave={() => !isDragOver && handleClose()}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <IconFile size="1rem" className={classes.fileBadgeIcon}/>
                    <Text color={"dimmed"}>Files</Text>
                    <Text className={classes.fileBadgeCount}>
                        {fileCount}{uploadsInProgress > 0 ?
                        <sup style={{fontSize: "0.7em" }}>+{uploadsInProgress}</sup> : ""}
                    </Text>
                </Box>
            </Popover.Target>
            <Popover.Dropdown

                onMouseEnter={() => setOpened(true)}
                onMouseLeave={handleClose}
            >
                <Box className={classes.filePopover}>
                    <Box style={{display: "flex", justifyContent: "flex-end", padding: "0.25rem 0.5rem 0"}}>
                        <Tooltip withinPortal={false} label={isPinned ? "Unpin popover" : "Pin popover open"}>
                            <ActionIcon
                                size="xs"
                                variant={isPinned ? "filled" : "subtle"}
                                color={isPinned ? "blue" : "gray"}
                                onClick={() => setIsPinned(!isPinned)}
                            >
                                {isPinned ? <IconPin size="0.875rem"/> : <IconPinnedOff size="0.875rem"/>}
                            </ActionIcon>
                        </Tooltip>
                    </Box>
                    {disabled ? (
                        <Box p="sm">
                            <Text size="sm" c="dimmed" ta="center">
                                Files will be collected during protocol execution
                            </Text>
                        </Box>
                    ) : isProtocolStep && subjectId ? (
                        <StepFileManager subjectId={subjectId} step={step as IProtocolStep} disabled={disabled}/>
                    ) : (
                        <Box p="sm">
                            <Text size="sm" c="dimmed" ta="center">
                                No file manager available
                            </Text>
                        </Box>
                    )}
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
};